// Lib
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// Components
import CountButton from '../../Button/CountButton';
import Button from '../../Button';
import CountButtonLabel from '../../Labels/CountButtonLabel';

// Constants
import { MORE_THAN_IN_STOCK_MESSAGE } from '../../../constants/products';

// Utils
import isMoreThanInStock from '../../../utils/isMoreThanInStock';
import isDigitalAsset from '../../../utils/isDigitalAsset';

// Hooks
import useUI from '../../../hooks/useUI';
import useUser from '../../../hooks/useUser';

import { useStyles } from './MaterialsProductQtyButtonsStyles';

const MaterialsProductQtyButtons = ({
  product,
  addInCart,
  count,
  addOne,
  removeOne,
  setCount,
  isProductInCart,
  listView,
  detailsMode, // in products modal details
  className,
  loading = false,
}) => {
  const { pathname } = useLocation();
  const { isMobileMode, isTabletMode, isDesktopMode } = useUI();
  const { isKiteUser } = useUser();

  const isRecurringMaterials = useMemo(() => {
    if (
      pathname.includes('/order-history/recurring/') &&
      pathname.includes('/materials')
    ) {
      return true;
    }
  }, [pathname]);

  const { classes, cx } = useStyles({
    detailsMode,
    listView,
    isRecurringMaterials,
  });

  const actionButtonTitle = useMemo(() => {
    const instance = isRecurringMaterials ? 'Order' : 'Cart';

    return !isProductInCart ? `Add To ${instance}` : `In ${instance}`;
  }, [isProductInCart, isRecurringMaterials]);

  const labelVariant = useMemo(() => {
    let variant = isMobileMode ? 'textLg' : 'textSm';
    if (!isKiteUser) {
      variant += 'Semibold';
    }
    return variant;
  }, [isKiteUser, isMobileMode]);

  const isMoreThanInStockProduct = useMemo(
    () => isMoreThanInStock(product, count),
    [product, count],
  );

  const isAddToCartButtonDisabled = useMemo(() => {
    if (isDigitalAsset(product)) {
      return isProductInCart;
    }

    return (
      isProductInCart ||
      (!isMobileMode && !detailsMode && count === 0) ||
      (detailsMode && count === 0)
    );
  }, [count, detailsMode, isMobileMode, isProductInCart, product]);

  const isQtyLabelShown = useMemo(() => {
    return isMoreThanInStockProduct && !(isMobileMode && !detailsMode);
  }, [detailsMode, isMobileMode, isMoreThanInStockProduct]);

  return (
    <div className={cx(classes.container, className)}>
      {(detailsMode || (!detailsMode && !isMobileMode)) && (
        <CountButton
          product={product}
          count={count}
          onAdd={addOne(product)}
          onRemove={removeOne(product)}
          onSetCount={setCount}
          error={isMoreThanInStockProduct}
          className={classes.countButton}
        />
      )}
      {isQtyLabelShown ? (
        <CountButtonLabel
          className={classes.qtyButtonLabel}
          variant={labelVariant}>
          {(isTabletMode || (isDesktopMode && !listView)) && !detailsMode ? (
            <div>
              More Than <br /> In Stock
            </div>
          ) : (
            MORE_THAN_IN_STOCK_MESSAGE
          )}
        </CountButtonLabel>
      ) : (
        <Button
          loading={loading}
          id="add-to-cart"
          onClick={addInCart(product)}
          disabled={isAddToCartButtonDisabled}
          className={classes.addToCartButton}>
          {actionButtonTitle}
        </Button>
      )}
    </div>
  );
};

export default MaterialsProductQtyButtons;
